import { defineStore } from 'pinia'
import { computed, ComputedRef, ref } from 'vue'
import { type GeoJSONType, useSiteStore } from '@/storage/site'
import overlap from '@/components/use/distance'
import { useAppStore } from '@/storage/app'
import { maxScannedInDistance } from '@/config/distance'
import { useCustomerStore } from '@/storage/customer'
import InvalidStoreException from '@/exceptions/InvalidStoreException'
import { getLocationRequiredForUserType } from '@/services/api/customer'
import { useUserStore } from '@/storage/user'
import { LocationRequirementStatus } from '@/types/locationRequirementStatuses'
import { useBypassStore } from '@/storage/bypass'

export const useUserPositionStore = defineStore(
  'user/position',
  () => {
    /** state */
    const position = ref<Position>({
      accuracy: 0,
      altitude: 0,
      latitude: 0,
      longitude: 0,
    })
    const positionErrorMessage = ref<string[] | null>(null)

    /** getters */
    const isUserOnSite: ComputedRef<boolean> = computed(() => {
      const siteStore = useSiteStore()
      const boundary =
        siteStore.locationBoundary ??
        ({
          coordinates: [siteStore.longitude, siteStore.latitude],
          type: 'Point',
        } satisfies GeoJSONType)

      const user = {
        latitude: position.value.latitude,
        longitude: position.value.longitude,
      }
      return overlap(user, boundary, maxScannedInDistance)
    })

    const userHasLocationAndIsNotOnSite = computed(() => {
      return (
        position.value.latitude !== 0 &&
        position.value.longitude !== 0 &&
        !isUserOnSite.value
      )
    })

    const isUserPositionEmpty: ComputedRef<boolean> = computed(
      () => position.value.latitude === 0 || position.value.longitude === 0,
    )

    /** actions */
    async function setUserPosition(): Promise<void> {
      try {
        setIsLoading(true)
        const currentPosition = await generateNewPosition()
        position.value = {
          accuracy: currentPosition.coords.accuracy,
          altitude: currentPosition.coords.altitude,
          latitude: currentPosition.coords.latitude,
          longitude: currentPosition.coords.longitude,
        }
      } catch (error: unknown) {
        resetUserPosition()
        retrievePositionError(error as GeolocationPositionError)
        throw error
      } finally {
        setIsLoading(false)
      }
    }

    function retrievePositionError(error: GeolocationPositionError): void {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setPositionErrorMessage([
            'Location permission denied.',
            'Please turn on location services in your settings.',
          ])
          break
        case error.POSITION_UNAVAILABLE:
          setPositionErrorMessage([
            'Location currently unavailable.',
            'Please move to a suitable position and try again.',
          ])
          break
        case error.TIMEOUT:
          setPositionErrorMessage([
            'Unable to get location due to timeout.',
            'Please move to a suitable position and try again.',
          ])
          break
        default:
          setPositionErrorMessage([
            'Please turn on location services in your settings.',
          ])
          break
      }
    }

    function setIsLoading(value: boolean) {
      const appStore = useAppStore()
      appStore.loading.set('position', value)
    }

    async function checkLocationRequirements(): Promise<LocationRequirementStatus> {
      const { scanBypassed } = useBypassStore()

      if (scanBypassed) {
        return LocationRequirementStatus.Passed
      }

      const id = useCustomerStore().id
      const selectedUserTypeId = useUserStore().userType.id

      if (id === 0 || selectedUserTypeId === 0) {
        throw new InvalidStoreException()
      }

      const required = await getLocationRequiredForUserType(
        id,
        selectedUserTypeId,
      )

      if (!required) {
        return LocationRequirementStatus.Passed
      }

      try {
        await setUserPosition()
      } catch {
        return LocationRequirementStatus.PositionError
      }

      if (position.value.accuracy > 250) {
        return LocationRequirementStatus.NotPrecise
      }

      if (!isUserOnSite.value) {
        return LocationRequirementStatus.NotOnSite
      }

      return LocationRequirementStatus.Passed
    }

    const resetUserPosition = () => {
      position.value = {
        accuracy: 0,
        altitude: 0,
        latitude: 0,
        longitude: 0,
      }
    }

    function setPositionErrorMessage(message: string[]) {
      positionErrorMessage.value = message
    }

    const resetPositionErrorMessage = () => {
      positionErrorMessage.value = null
    }

    return {
      checkLocationRequirements,
      isUserOnSite,
      isUserPositionEmpty,
      position,
      positionErrorMessage,
      resetPositionErrorMessage,
      resetUserPosition,
      setIsLoading,
      setPositionErrorMessage,
      setUserPosition,
      userHasLocationAndIsNotOnSite,
    }
  },
  {
    persist: true,
  },
)

function generateNewPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        maximumAge: 0, // 0 = disable cache to request new location each time
        timeout: 30000, // 30 seconds
      },
    )
  }) as Promise<GeolocationPosition>
}
